/**
 * Рулетка: вращения за оплату, анимация колеса, выигранные призы.
 *
 * Приз выбирает сервер в момент нажатия — колесо лишь докручивается до
 * выпавшего сектора. Пока идёт вращение, кнопка заблокирована: второе
 * нажатие сервер всё равно не пропустит (вращение — под блокировкой).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { spinWheel, spinsWord, useWheel } from '@/features/wheel/wheel';
import { messageOf } from '@/features/payment/usePayStars';
import type { WheelSpin } from '@/shared/api/schemas';
import { formatShortDate } from '@/shared/lib/format';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Sheet } from '@/shared/ui/kit/Sheet';

import styles from './Wheel.module.css';

/** Короткие подписи секторов — по порядку с сервера. */
const LABEL: Record<string, string> = {
  discount20: '−20%',
  discount50: '−50%',
  month1: '1 мес',
  month3: '3 мес',
  month6: '6 мес',
  iphone: 'iPhone',
};
const SPIN_MS = 4200;
const TURNS = 6;

const DISCOUNT_STATUS: Record<string, string> = {
  active: 'ждёт оплаты',
  used: 'использована',
  expired: 'истекла',
  voided: 'отменена',
};

function resultText(r: WheelSpin): { title: string; text: string } {
  if (r.kind === 'discount' && r.discount) {
    return {
      title: r.title,
      text: `Применится сама к следующей оплате подписки. Действует до ${formatShortDate(r.discount.expires_at)}.`,
    };
  }
  if (r.kind === 'months') {
    return {
      title: r.title,
      text: r.subscription_until
        ? `Добавлено к вашей подписке — теперь до ${formatShortDate(r.subscription_until)}.`
        : 'Добавлено к вашей подписке.',
    };
  }
  return {
    title: 'iPhone 18 Pro Max Burgundy!',
    text: 'Главный приз! Мы напишем вам в Telegram в течение 24 часов. Если отправить приз невозможно — выплатим его стоимость.',
  };
}

export function WheelPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useWheel();
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<WheelSpin | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const busy = useRef(false);
  useBackButton(() => {
    if (result) setResult(null);
    else void navigate('/profile');
  });

  const segments = data?.segments ?? [];
  const step = segments.length ? 360 / segments.length : 60;
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const spin = async () => {
    if (busy.current || !data || data.spins_left < 1) return;
    busy.current = true;
    setFailure(null);
    setSpinning(true);
    try {
      const won = await spinWheel();
      // Центр выпавшего сектора — под стрелку сверху.
      const center = won.segment * step + step / 2;
      const current = ((rotation % 360) + 360) % 360;
      const delta = (360 - center - current + 720) % 360;
      setRotation(rotation + TURNS * 360 + delta);
      window.setTimeout(
        () => {
          setResult(won);
          setSpinning(false);
          busy.current = false;
          void queryClient.invalidateQueries({ queryKey: ['wheel'] });
          void queryClient.invalidateQueries({ queryKey: ['subscription'] });
        },
        reduced ? 50 : SPIN_MS,
      );
    } catch (caught) {
      setFailure(messageOf(caught));
      setSpinning(false);
      busy.current = false;
    }
  };

  const gradient = segments
    .map((s, i) => {
      const color = s.code === 'iphone' ? '#3a1018' : i % 2 === 0 ? '#2a2412' : '#161616';
      return `${color} ${i * step}deg ${(i + 1) * step}deg`;
    })
    .join(', ');
  const content = result ? resultText(result) : null;

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <h1 className={styles.head}>Рулетка</h1>
      <p className={styles.intro}>Вращения начисляются за оплату подписки: 1 месяц — 1, 1 год — 12.</p>

      <div className={styles.wheelBox}>
        <span className={styles.pointer} aria-hidden />
        <div
          className={styles.wheel}
          style={{
            background: `conic-gradient(${gradient})`,
            transform: `rotate(${rotation}deg)`,
            transition: spinning && !reduced ? `transform ${SPIN_MS}ms cubic-bezier(0.17, 0.67, 0.12, 1)` : 'none',
          }}
          aria-hidden
        >
          {segments.map((s, i) => (
            <span
              key={s.code}
              className={s.code === 'iphone' ? `${styles.label} ${styles.labelJackpot}` : styles.label}
              style={{ transform: `rotate(${i * step + step / 2}deg) translateY(-112px)` }}
            >
              {LABEL[s.code] ?? s.title}
            </span>
          ))}
        </div>
        <span className={styles.hub}>ANROYAL</span>
      </div>

      <Button block disabled={spinning || !data || data.spins_left < 1} onClick={() => void spin()}>
        {spinning
          ? 'Крутится…'
          : data && data.spins_left > 0
            ? `Крутить · осталось ${data.spins_left}`
            : 'Вращений нет'}
      </Button>
      {failure ? <p className={styles.error} role="alert">{failure}</p> : null}
      {data && data.spins_left < 1 && !spinning ? (
        <Button block variant="quiet" onClick={() => void navigate('/plans')}>
          Получить вращения — оплатить подписку
        </Button>
      ) : null}

      {data?.best_discount ? (
        <div className={styles.discount}>
          Скидка {data.best_discount.percent}% применится к следующей оплате — до{' '}
          {formatShortDate(data.best_discount.expires_at)}
        </div>
      ) : null}

      {data && (data.history.length > 0 || data.discounts.length > 0) ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Мои призы</h2>
          {data.discounts.map((d, i) => (
            <div key={`d-${i}`} className={styles.row}>
              <span>Скидка {d.percent}%</span>
              <span className={styles.muted}>
                {DISCOUNT_STATUS[d.status] ?? d.status}
                {d.status === 'active' ? ` · до ${formatShortDate(d.expires_at)}` : ''}
              </span>
            </div>
          ))}
          {data.history
            .filter((h) => !h.code.startsWith('discount'))
            .map((h, i) => (
              <div key={`h-${i}`} className={styles.row}>
                <span>{h.title}</span>
                <span className={styles.muted}>{formatShortDate(h.spun_at)}</span>
              </div>
            ))}
        </section>
      ) : null}

      <Sheet open={result !== null} title={content?.title ?? ''} onClose={() => setResult(null)} fit>
        <p className={styles.resultText}>{content?.text}</p>
        {result && result.spins_left > 0 ? (
          <Button block onClick={() => setResult(null)}>
            Крутить ещё · осталось {result.spins_left} {spinsWord(result.spins_left)}
          </Button>
        ) : (
          <Button block onClick={() => setResult(null)}>
            Готово
          </Button>
        )}
      </Sheet>
    </Screen>
  );
}
